import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import WelcomeStep from './steps/WelcomeStep';
import ProfileRequiredStep from './steps/ProfileRequiredStep';
import ProfileOptionalStep from './steps/ProfileOptionalStep';
import CareerGoalsStep from './steps/CareerGoalsStep';
import CompleteStep from './steps/CompleteStep';
import ErrorBoundary from '../common/ErrorBoundary';

const AutoAdvanceFallback = ({ error, onNext }) => {
    useEffect(() => {
        console.error("Tour step crashed, auto-advancing to next step:", error);
        onNext();
    }, [error, onNext]);
    return null;
};

const OnboardingFlow = ({ onComplete, userFirstName, prefilledData = null, onTryAI = null, onSkip = null }) => {
    // If data was prefilled from AI onboarding, the profile is already gathered —
    // skip straight to the final 'complete' step (index 4 in the steps array).
    const prefillStartStep = 4;
    const hasPrefill = Boolean(prefilledData && typeof prefilledData === 'object');
    const [currentStep, setCurrentStep] = useState(hasPrefill ? prefillStartStep : 0);
    const [profileData, setProfileData] = useState({
        // Required fields — prefilled from AI onboarding when available
        experienceLevel: prefilledData?.experienceLevel || '',
        desiredRole: prefilledData?.desiredRole || '',
        skills: Array.isArray(prefilledData?.skills) ? prefilledData.skills : [],
        locationPreference: prefilledData?.locationPreference || '',
        
        // Optional fields
        industryPreference: prefilledData?.industryPreference || (Array.isArray(prefilledData?.preferredIndustries) ? prefilledData.preferredIndustries.join(', ') : ''),
        preferredIndustries: Array.isArray(prefilledData?.preferredIndustries) ? prefilledData.preferredIndustries : [],
        preferredIndustry: Array.isArray(prefilledData?.preferredIndustry) ? prefilledData.preferredIndustry : [],
        preferredDepartments: Array.isArray(prefilledData?.preferredDepartments) ? prefilledData.preferredDepartments : [],
        preferredDepartment: Array.isArray(prefilledData?.preferredDepartment) ? prefilledData.preferredDepartment : [],
        companySizePreference: '',
        salaryExpectation: prefilledData?.salaryExpectation || '',
        remotePreference: prefilledData?.remotePreference || '',
        
        // Career goals fields
        careerGoal: '',
        prioritizeSmallCompanies: undefined,
        prioritizeHighMorale: undefined,
        currentSalary: '',
        prioritizeHigherPay: undefined
    });

    const steps = [
        {
            id: 'welcome',
            title: `Welcome to Career Champ, ${userFirstName}`,
            subtitle: 'Your AI-powered job search companion',
            component: WelcomeStep,
            isInteractive: false
        },
        {
            id: 'profile-required',
            title: "Build Your Career Profile",
            subtitle: 'Help us match you with the perfect opportunities',
            component: ProfileRequiredStep,
            required: true,
            isInteractive: false
        },
        {
            id: 'profile-optional',
            title: 'Refine Your Preferences',
            subtitle: 'Optional preferences to personalize your experience',
            component: ProfileOptionalStep,
            required: false,
            isInteractive: false
        },
        {
            id: 'career-goals',
            title: 'Your Career Goals',
            subtitle: 'Personalize recommendations based on your priorities',
            component: CareerGoalsStep,
            required: false,
            isInteractive: false
        },
        {
            id: 'complete',
            title: "You're Ready to Get Hired",
            subtitle: 'Start your accelerated job search journey',
            component: CompleteStep,
            isInteractive: false
        }
    ];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(Math.min(steps.length - 1, currentStep + 1));
        } else {
            handleComplete();
        }
    };

    const handleSkip = () => {
        // Skip current step and move to next
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            handleComplete();
        }
    };

    const handleSkipAll = () => {
        // Skip entire onboarding
        handleComplete();
    };

    const handleComplete = () => {
        // Mark onboarding as complete
        localStorage.setItem('hasCompletedOnboarding', 'true');
        
        // Clean up demo data from Redux - real data will be fetched automatically
        // when isOnboardingActive becomes false, the normal fetch logic will run
        onComplete(profileData);
    };

    const CurrentStepComponent = steps[currentStep].component;
    const currentStepData = steps[currentStep];

    return (
        <div className="fixed inset-0 z-[100] bg-gray-900 bg-opacity-95 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full h-full relative">
                {/* Allow page content to be visible */}
                <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
                    {/* This div allows the page behind to be visible */}
                </div>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="absolute inset-0"
                        style={{ zIndex: 2 }}
                    >
                        <ErrorBoundary fallback={({ error }) => <AutoAdvanceFallback error={error} onNext={handleNext} />}>
                            <CurrentStepComponent
                                profileData={profileData}
                                setProfileData={setProfileData}
                                onNext={handleNext}
                                onSkip={currentStepData.id === 'welcome' && onSkip ? onSkip : handleSkip}
                                onTryAI={currentStepData.id === 'welcome' ? onTryAI : undefined}
                            />
                        </ErrorBoundary>
                    </motion.div>
                </AnimatePresence>

                {/* Progress indicator at bottom — hidden on the final "complete" step */}
                {currentStepData.id !== 'complete' && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[10000] bg-white/90 backdrop-blur-sm rounded-full px-6 py-2 shadow-lg flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">
                            Step {currentStep + 1} of {steps.length}
                        </span>
                        <div className="flex gap-1">
                            {steps.map((_, index) => (
                                <div
                                    key={index}
                                    className={`w-2 h-2 rounded-full transition-all ${
                                        index === currentStep ? 'bg-blue-600 w-6' :
                                        index < currentStep ? 'bg-blue-400' : 'bg-gray-300'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                    {/* Skip button */}
                    <button
                        onClick={handleSkipAll}
                        className="text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors px-3 py-1 rounded-lg hover:bg-gray-100"
                    >
                        Skip All
                    </button>
                </div>
                )}
            </div>
        </div>
    );
};

export default OnboardingFlow;
